import * as signalR from '@microsoft/signalr';

const API_URL = process.env.REACT_APP_API_URL;

class SignalRService {
  constructor() {
    this.connection = null;
    this.connectionPromise = null; // Use a promise to track connection initialization
    this.isInitializing = false;
  }

  // Initialize the connection if it's not already initialized
  async initialize(hubPath) {
    // If connection already exists and is connected, return it
    if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
      return this.connection;
    }

    // If there's already an initialization in progress, wait for it
    if (this.isInitializing && this.connectionPromise) {
      return this.connectionPromise;
    }

    // Start new initialization
    this.isInitializing = true;
    
    try {
      // Extract base URL without "/api" suffix for SignalR connections
      const baseUrl = API_URL.endsWith('/api') 
        ? API_URL.substring(0, API_URL.length - 4) 
        : API_URL;
      
      // Close any existing connection before creating a new one
      if (this.connection) {
        try {
          await this.connection.stop();
        } catch (stopError) {
          console.warn('Error stopping previous connection:', stopError);
        }
      }
      
      // Create a new connection with the correct hub URL
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(baseUrl + hubPath)
        .withAutomaticReconnect([0, 2000, 5000, 10000, 20000])
        .configureLogging(signalR.LogLevel.Information)
        .build();
      
      // Start the connection
      this.connectionPromise = this.connection.start()
        .then(() => {
          this.isInitializing = false;
          return this.connection;
        })
        .catch((err) => {
          console.error('SignalR Connection Error: ', err);
          this.isInitializing = false;
          this.connection = null;
          throw err;
        });
      
      return this.connectionPromise;
    } catch (error) {
      console.error('SignalR initialization error: ', error);
      this.isInitializing = false;
      throw error;
    }
  }

  // Check if connection exists and is connected
  isConnected() {
    return this.connection && this.connection.state === signalR.HubConnectionState.Connected;
  }

  // Get the connection ID if connected
  getConnectionId() {
    if (this.isConnected()) {
      return this.connection.connectionId;
    }
    return null;
  }

  // Close the connection
  async closeConnection() {
    if (this.connection) {
      try {
        await this.connection.stop();
        this.connection = null;
        this.connectionPromise = null;
      } catch (error) {
        console.error('Error closing SignalR connection: ', error);
      }
    }
  }
}

const signalRService = new SignalRService();

export default signalRService;